import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CoordinateSystemModel } from '../../../../../../../../../typings/api/skymap/rest/v0/.common';
import { Coordinates } from '../../../../../../../viewer/utilities/math-helpers';
import { ProjectSpatialReferenceContext } from '../../../../../../state/project-reference-state';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Stack } from '../../../../../stack/stack';
import { CollapsableSection } from '../../../components/collapsable-section';
import { CoordinateValidationInput } from '../coordinate-validation-input';

type Props = {
  knownCoordinateA: Coordinates;
  knownCoordinateB: Coordinates;
  setKnownCoordinateA: (point: Coordinates) => void;
  setKnownCoordinateB: (point: Coordinates) => void;
  setValid: (valid: boolean) => void;
};

export const KnownCooridnatesSection = (props: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(true);
  const [validA, setValidA] = React.useState(true);
  const [validB, setValidB] = React.useState(true);

  const { getCoordinateSystemOrDefault } = React.useContext(ProjectSpatialReferenceContext);
  const coordinateSystem: CoordinateSystemModel = getCoordinateSystemOrDefault();

  return (
    <CollapsableSection
      expanded={expanded}
      title={t('section.knownCoordinates.text', { ns: 'skyviewScale' })}
      tooltip={{
        text: (
          <Trans
            components={{ br: <br />, bold: <strong /> }}
            i18nKey="section.knownCoordinates.info.text"
            ns="skyviewScale"
          />
        ),
        title: t('section.knownCoordinates.info.title', { ns: 'skyviewScale' }),
      }}
      onSetExpanded={(state) => setExpanded(state)}
    >
      <Stack spacing={0.8}>
        <LabelledContainer text={t('section.knownCoordinates.coordinateA', { ns: 'skyviewScale' })}>
          <CoordinateValidationInput
            coordinateSystem={coordinateSystem}
            defaultValue={{
              x: props.knownCoordinateA.northing,
              y: props.knownCoordinateA.easting,
              z: props.knownCoordinateA.height,
            }}
            leftBorderColor="blue"
            showHeight={true}
            onValidate={(valid: boolean) => {
              setValidA(valid);
              props.setValid(valid && validB);
            }}
            onValueChanged={(coordinate: Coordinates) => {
              props.setKnownCoordinateA(coordinate);
            }}
          />
        </LabelledContainer>

        <LabelledContainer text={t('section.knownCoordinates.coordinateB', { ns: 'skyviewScale' })}>
          <CoordinateValidationInput
            coordinateSystem={coordinateSystem}
            defaultValue={{
              x: props.knownCoordinateB.northing,
              y: props.knownCoordinateB.easting,
              z: props.knownCoordinateA.height,
            }}
            leftBorderColor="green"
            onValidate={(valid: boolean) => {
              setValidB(valid);
              props.setValid(valid && validA);
            }}
            onValueChanged={(coordinate: Coordinates) => {
              coordinate.height = props.knownCoordinateA.height;
              props.setKnownCoordinateB(coordinate);
            }}
          />
        </LabelledContainer>
      </Stack>
    </CollapsableSection>
  );
};
